'use client';

import { c as _c } from "react/compiler-runtime";
import type { ExtractAtomValue } from 'jotai';
import type { Layout as ResizableLayout } from 'react-resizable-panels';
import { useIsomorphicLayoutEffect } from '@react-hookz/web';
import clsx from 'clsx';
import { pipe } from 'effect';
import { atom, useAtom, useAtomValue } from 'jotai';
import Link from 'next/link';
import * as React from 'react';
import { Fragment, useDeferredValue, useEffectEvent, useRef } from 'react';
import { useGroupRef } from 'react-resizable-panels';
import getCaretCoordinates from 'textarea-caret';
import type { Sentence } from '@/app/[locale]/(vocabulary)/_lib/LexiconTrie';
import type { VocabularySourceData } from '@/app/[locale]/(vocabulary)/_lib/vocab';
import { baseVocabAtom, irregularWordsQueryAtom } from '@/app/[locale]/(vocabulary)/_api';
import { isSourceTextStaleAtom } from '@/app/[locale]/(vocabulary)/_atoms';
import { VocabSourceTable } from '@/app/[locale]/(vocabulary)/_components/source';
import { VocabStatics } from '@/app/[locale]/(vocabulary)/_components/statics-bar';
import { LEARNING_PHASE, LexiconTrie } from '@/app/[locale]/(vocabulary)/_lib/LexiconTrie';
import { fileTypesAtom } from '@/atoms/file-types';
import { fileInfoAtom, sourceTextAtom } from '@/atoms/vocabulary';
import { FileInput } from '@/components/file-input';
import { FileSettings } from '@/components/file-settings';
import { NoSSR } from '@/components/NoSsr';
import { Button } from '@/components/ui/button';
import { OverflowTooltipText } from '@/components/ui/overflow-tooltip-text';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { TextareaInput } from '@/components/ui/textarea-input';
import { useI18n } from '@/locales/client';
import { mediaQueryFamily } from '@sub-vocab/utils/atoms';
import { useAtomEffect } from '@sub-vocab/utils/hooks';
import { compareBy, normalizeNewlines, tap } from '@sub-vocab/utils/lib';
const sourceCountAtom = atom({
  totalText: 0,
  acquainted: 0,
  newCount: 0
});
sourceCountAtom.debugLabel = 'sourceCountAtom';
function getCount(list: VocabularySourceData[]): ExtractAtomValue<typeof sourceCountAtom> {
  let acquaintedCount = 0;
  let newCount = 0;
  let rest = 0;
  for (const {
    trackedWord,
    locators
  } of list) {
    if (trackedWord.learningPhase === LEARNING_PHASE.ACQUAINTED) {
      acquaintedCount += locators.length;
    } else if (trackedWord.learningPhase === LEARNING_PHASE.NEW) {
      newCount += locators.length;
    } else {
      rest += locators.length;
    }
  }
  return {
    acquainted: acquaintedCount,
    newCount,
    totalText: acquaintedCount + newCount + rest
  };
}
function SourceVocab(t0) {
  const $ = _c(19);
  const {
    text: t1,
    onSentenceTrack
  } = t0;
  const {
    value: text
  } = t1;
  const {
    data: t2
  } = useAtomValue(irregularWordsQueryAtom);
  let t3;
  if ($[0] !== t2) {
    t3 = t2 === undefined ? [] : t2;
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const irregulars = t3;
  const [baseVocab] = useAtom(baseVocabAtom);
  let t4;
  let trie;
  if ($[2] !== baseVocab || $[3] !== irregulars || $[4] !== text) {
    let t5;
    if ($[7] !== text) {
      t5 = x => {
        x.add(text);
      };
      $[7] = text;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    trie = pipe(new LexiconTrie(), tap(t5));
    t4 = trie.generate(irregulars, baseVocab).filter(_temp).sort(compareBy(_temp2));
    $[2] = baseVocab;
    $[3] = irregulars;
    $[4] = text;
    $[5] = t4;
    $[6] = trie;
  } else {
    t4 = $[5];
    trie = $[6];
  }
  const list = t4;
  const sentences = trie.sentences;
  let t5;
  let t6;
  if ($[9] !== list) {
    t5 = (_, set) => {
      set(sourceCountAtom, getCount(list));
    };
    t6 = [list];
    $[9] = list;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  useAtomEffect(t5, t6);
  let t7;
  if ($[12] !== onSentenceTrack || $[13] !== sentences) {
    t7 = no => {
      const sentence = sentences[no];
      if (sentence) {
        onSentenceTrack(sentence);
      }
    };
    $[12] = onSentenceTrack;
    $[13] = sentences;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== list || $[16] !== sentences || $[17] !== t7) {
    t8 = <VocabSourceTable data={list} sentences={sentences} onSentenceTrack={t7} className="h-full" />;
    $[15] = list;
    $[16] = sentences;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
}
function _temp2(i) {
  return [i.locators[0].sentenceId, i.locators[0].startOffset];
}
function _temp(v) {
  return v.locators.length > 0;
}
const horizontalDefaultSizesAtom = atom([50, 50]);
horizontalDefaultSizesAtom.debugLabel = 'horizontalDefaultSizesAtom';
const verticalDefaultSizesAtom = atom([36, 64]);
verticalDefaultSizesAtom.debugLabel = 'verticalDefaultSizesAtom';
const sourceInputPanelId = 'source-input';
const sourceVocabPanelId = 'source-vocab';
function getResizableLayout(sizes: number[]): ResizableLayout {
  return {
    [sourceInputPanelId]: sizes[0] ?? 50,
    [sourceVocabPanelId]: sizes[1] ?? 50
  };
}
export default function Layout() {
  const $ = _c(69);
  const t = useI18n();
  const [fileInfo, setFileInfo] = useAtom(fileInfoAtom);
  const [sourceText, setSourceText] = useAtom(sourceTextAtom);
  const deferredSourceText = useDeferredValue(sourceText);
  let t0;
  let t1;
  if ($[0] !== deferredSourceText.epoch) {
    t0 = (get, set) => {
      const isStale = get(sourceTextAtom).epoch !== deferredSourceText.epoch;
      if (get(isSourceTextStaleAtom) !== isStale) {
        set(isSourceTextStaleAtom, isStale);
      }
    };
    t1 = [deferredSourceText.epoch];
    $[0] = deferredSourceText.epoch;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useAtomEffect(t0, t1);
  let t2;
  if ($[3] !== setSourceText) {
    t2 = function handleTextareaChange(ev) {
      setSourceText(v => ({
        value: ev.target.value,
        epoch: v.epoch + 1
      }));
    };
    $[3] = setSourceText;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleTextareaChange = t2;
  const [t3] = useAtom(sourceCountAtom);
  const {
    totalText: count,
    acquainted: acquaintedWordCount,
    newCount: newWordCount
  } = t3;
  const [horizontalDefaultSizes, setHorizontalDefaultSizes] = useAtom(horizontalDefaultSizesAtom);
  const [verticalDefaultSizes, setVerticalDefaultSizes] = useAtom(verticalDefaultSizesAtom);
  const isLgScreen = useAtomValue(mediaQueryFamily.useA("(min-width: 1024px)"));
  const direction = isLgScreen ? "horizontal" : "vertical";
  const defaultSizes = direction === "vertical" ? verticalDefaultSizes : horizontalDefaultSizes;
  const panelGroupRef = useGroupRef();
  let t4;
  if ($[5] !== defaultSizes || $[6] !== panelGroupRef.current) {
    t4 = () => {
      const panelGroup = panelGroupRef.current;
      if (panelGroup) {
        panelGroup.setLayout(getResizableLayout(defaultSizes));
      }
    };
    $[5] = defaultSizes;
    $[6] = panelGroupRef.current;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const resetPanelLayout = useEffectEvent(t4);
  let t5;
  if ($[8] !== resetPanelLayout) {
    t5 = () => {
      resetPanelLayout();
    };
    $[8] = resetPanelLayout;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== direction) {
    t6 = [direction];
    $[10] = direction;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  useIsomorphicLayoutEffect(t5, t6);
  let t7;
  if ($[12] !== direction || $[13] !== setHorizontalDefaultSizes || $[14] !== setVerticalDefaultSizes) {
    t7 = function handleLayoutChange(layout) {
      const sourceInputSize = layout[sourceInputPanelId];
      const sourceVocabSize = layout[sourceVocabPanelId];
      if (typeof sourceInputSize !== "number" || typeof sourceVocabSize !== "number") {
        return;
      }
      const nextSizes = [sourceInputSize, sourceVocabSize];
      if (direction === "vertical") {
        setVerticalDefaultSizes(nextSizes);
      } else {
        setHorizontalDefaultSizes(nextSizes);
      }
    };
    $[12] = direction;
    $[13] = setHorizontalDefaultSizes;
    $[14] = setVerticalDefaultSizes;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handleLayoutChange = t7;
  const [fileTypes] = useAtom(fileTypesAtom);
  let t8;
  if ($[16] !== setFileInfo || $[17] !== setSourceText) {
    t8 = function handleFileChange(t9) {
      const {
        name,
        value
      } = t9;
      setSourceText(v_0 => ({
        value: normalizeNewlines(value),
        epoch: v_0.epoch + 1
      }));
      if (name) {
        setFileInfo(name);
      }
    };
    $[16] = setFileInfo;
    $[17] = setSourceText;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const handleFileChange = t8;
  const textareaRef = useRef(null);
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = function onSentenceTrack(sentence) {
      const textarea = textareaRef.current;
      if (textarea) {
        requestAnimationFrame(() => {
          const coords = getCaretCoordinates(textarea, sentence.index);
          textarea.focus();
          textarea.setSelectionRange(sentence.index, sentence.index + sentence.text.length);
          textarea.scrollTo({
            top: coords.top - 4,
            behavior: "smooth"
          });
        });
      }
    };
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const onSentenceTrack = t9;
  let t10;
  if ($[20] !== t) {
    t10 = t("browseFiles");
    $[20] = t;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== handleFileChange || $[23] !== t10) {
    t11 = <FileInput onFileSelect={handleFileChange} className="*:*:text-xs *:*:data-[slot=button]:h-8 *:*:data-[slot=button]:px-3">{t10}</FileInput>;
    $[22] = handleFileChange;
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <FileSettings />;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  let t14;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Button variant="secondary" className="h-8 px-3 text-xs" asChild={true}><Link href="/subtitles">Subtitles</Link></Button>;
    t14 = <div className="grow" />;
    $[26] = t13;
    $[27] = t14;
  } else {
    t13 = $[26];
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t11) {
    t15 = <div className="pb-3"><div className="flex items-center gap-2">{t11}{t12}{t13}{t14}</div></div>;
    $[28] = t11;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = clsx("[body:has(&[data-panel-group-direction=vertical])]:overflow-hidden");
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const t17 = `${defaultSizes[0]}%`;
  let t18;
  if ($[31] !== fileInfo) {
    t18 = <div className="flex h-12 shrink-0 items-center bg-background py-2 pr-2 pl-4 text-xs"><OverflowTooltipText value={fileInfo} delayDuration={400} wrapperClassName="grow truncate" sideOffset={-23} alignOffset={-13} maxWidthOffset={9} contentClassName="px-3 py-1.5 text-xs" /></div>;
    $[31] = fileInfo;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="z-10 h-px w-full border-b border-solid border-border shadow-[0_0.4px_2px_0_rgb(0_0_0/0.05)]" />;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  const t20 = sourceText.value;
  let t21;
  if ($[34] !== t) {
    t21 = t("inputArea");
    $[34] = t;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] !== fileTypes || $[37] !== handleFileChange || $[38] !== handleTextareaChange || $[39] !== sourceText.value || $[40] !== t21) {
    t22 = <div className="size-full grow text-base text-zinc-700 md:text-sm"><TextareaInput ref={textareaRef} value={t20} placeholder={t21} fileTypes={fileTypes} onChange={handleTextareaChange} onFileChange={handleFileChange} /></div>;
    $[36] = fileTypes;
    $[37] = handleFileChange;
    $[38] = handleTextareaChange;
    $[39] = sourceText.value;
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  const t23 = ` ${t("words")}`;
  let t24;
  if ($[42] !== acquaintedWordCount || $[43] !== count || $[44] !== newWordCount || $[45] !== t23) {
    t24 = <div className="flex w-full justify-center border-t border-solid border-t-zinc-200 bg-background dark:border-neutral-800"><VocabStatics total={count} text={t23} remaining={newWordCount} completed={acquaintedWordCount} progress={true} /></div>;
    $[42] = acquaintedWordCount;
    $[43] = count;
    $[44] = newWordCount;
    $[45] = t23;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  let t25;
  if ($[47] !== t18 || $[48] !== t22 || $[49] !== t24) {
    t25 = <div className="flex h-full items-center justify-center"><div className="relative flex h-full grow flex-col overflow-hidden">{t18}{t19}{t22}{t24}</div></div>;
    $[47] = t18;
    $[48] = t22;
    $[49] = t24;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== t17 || $[52] !== t25) {
    t26 = <ResizablePanel id={sourceInputPanelId} defaultSize={t17}>{t25}</ResizablePanel>;
    $[51] = t17;
    $[52] = t25;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <ResizableHandle withHandle={true} className="focus-visible:bg-ring focus-visible:ring-offset-[-1px]" />;
    $[54] = t27;
  } else {
    t27 = $[54];
  }
  const t28 = `${defaultSizes[1]}%`;
  let t29;
  if ($[55] !== deferredSourceText) {
    t29 = <SourceVocab key={deferredSourceText.epoch} text={deferredSourceText} onSentenceTrack={onSentenceTrack} />;
    $[55] = deferredSourceText;
    $[56] = t29;
  } else {
    t29 = $[56];
  }
  let t30;
  if ($[57] !== t28 || $[58] !== t29) {
    t30 = <ResizablePanel id={sourceVocabPanelId} defaultSize={t28}>{t29}</ResizablePanel>;
    $[57] = t28;
    $[58] = t29;
    $[59] = t30;
  } else {
    t30 = $[59];
  }
  let t31;
  if ($[60] !== direction || $[61] !== handleLayoutChange || $[62] !== panelGroupRef || $[63] !== t26 || $[64] !== t30) {
    t31 = <div className="flex grow items-center justify-center overflow-hidden rounded-xl border drop-shadow-xs sq:rounded-3xl"><NoSSR><ResizablePanelGroup groupRef={panelGroupRef} orientation={direction} className={t16} onLayoutChanged={handleLayoutChange}>{t26}{t27}{t30}</ResizablePanelGroup></NoSSR></div>;
    $[60] = direction;
    $[61] = handleLayoutChange;
    $[62] = panelGroupRef;
    $[63] = t26;
    $[64] = t30;
    $[65] = t31;
  } else {
    t31 = $[65];
  }
  let t32;
  if ($[66] !== t15 || $[67] !== t31) {
    t32 = <Fragment><Fragment>{t15}{t31}</Fragment></Fragment>;
    $[66] = t15;
    $[67] = t31;
    $[68] = t32;
  } else {
    t32 = $[68];
  }
  return t32;
}